import React from 'react';
import { TimerBadge } from '../TimerBadge/TimerBadge';
import { Badge } from '../../BasicComponents/Badge/Badge';
import type { TableCardProps, TableStatus, TableShape } from './types';

// ─── Status style config ──────────────────────────────────────────────────────

const STATUS_CLASSES: Record<TableStatus, string> = {
  available: 'border-success-400 bg-success-50 dark:bg-success-900/20 dark:border-success-500',
  occupied:  'border-brand-400 bg-brand-50 dark:bg-brand-900/20 dark:border-brand-500',
  reserved:  'border-warning-400 bg-warning-50 dark:bg-warning-900/20 dark:border-warning-500',
  cleaning:  'border-neutral-300 bg-neutral-100 dark:bg-neutral-800 dark:border-neutral-600',
};

const STATUS_LABELS: Record<TableStatus, string> = {
  available: 'Available',
  occupied:  'Occupied',
  reserved:  'Reserved',
  cleaning:  'Cleaning',
};

// ─── TableCard ────────────────────────────────────────────────────────────────

const SHAPE_CLASSES: Record<TableShape, string> = {
  rectangle: 'rounded-xl',
  square:    'rounded-md aspect-square',
  circle:    'rounded-full aspect-square',
};

export function TableCard({
  id,
  name,
  capacity,
  status,
  shape = 'rectangle',
  occupiedSince,
  guestCount,
  hasActiveOrder,
  orderNumber,
  onClick,
  className,
}: TableCardProps) {
  const elapsed = occupiedSince
    ? Math.floor((Date.now() - new Date(occupiedSince).getTime()) / 60000)
    : 0;

  return (
    <button
      type="button"
      role="button"
      aria-label={`Table ${name}, ${STATUS_LABELS[status]}${guestCount ? `, ${guestCount} guests` : ''}${occupiedSince ? `, ${elapsed} minutes` : ''}${hasActiveOrder ? ', has active order' : ''}`}
      onClick={() => onClick?.(id)}
      className={[
        'relative flex flex-col items-center justify-center gap-1.5 border-2 p-3 min-h-[80px] min-w-[80px]',
        SHAPE_CLASSES[shape],
        'transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        STATUS_CLASSES[status],
        onClick ? 'hover:shadow-md active:scale-95 cursor-pointer' : 'cursor-default',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Active order indicator (receipt badge) */}
      {hasActiveOrder && (
        <span
          data-testid="active-order-indicator"
          title={orderNumber ? `Active order ${orderNumber}` : 'Active order'}
          className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-pink-500 text-white shadow-md"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 2v20l2-1.5L8 22l2-1.5L12 22l2-1.5L16 22l2-1.5L20 22V2l-2 1.5L16 2l-2 1.5L12 2l-2 1.5L8 2 6 3.5 4 2z" />
            <line x1="8" y1="8" x2="16" y2="8" />
            <line x1="8" y1="12" x2="16" y2="12" />
            <line x1="8" y1="16" x2="13" y2="16" />
          </svg>
        </span>
      )}

      {/* Table name */}
      <span className="text-base font-bold text-neutral-800 dark:text-neutral-100">{name}</span>

      {/* Capacity */}
      <span className="text-xs text-neutral-500 dark:text-neutral-400">
        {guestCount !== undefined ? `${guestCount}/${capacity}` : `${capacity} seats`}
      </span>

      {/* Timer for occupied */}
      {status === 'occupied' && occupiedSince && (
        <TimerBadge startedAt={occupiedSince} />
      )}

      {/* Status badge for non-occupied */}
      {status !== 'occupied' && (
        <Badge
          variant={
            status === 'available' ? 'active' :
            status === 'reserved'  ? 'pending' : 'inactive'
          }
          size="sm"
        >
          {STATUS_LABELS[status]}
        </Badge>
      )}
    </button>
  );
}
